import { Avatar, Space, Tag, Tooltip } from "antd";
import {
  FONT,
  isAdmin,
  isEspotive,
  ROLE_ADMIN,
  ROLE_ADMIN_CUSTOMER,
  ROLE_USER,
  ROLE_USER_ADMIN,
} from "constants/index";
import {
  Info,
  KeySquare,
  UserRound,
  UserRoundPen,
  UserRoundX,
} from "lucide-react";
import dayjs from "dayjs";

export const ROLE_STYLES = {
  [ROLE_ADMIN]: {
    label: "Administrador",
    color: "#722ed1",
    bg: "#f9f0ff",
    border: "#d3adf7",
  },
  [ROLE_USER_ADMIN]: {
    label: "Professor",
    color: "#1677ff",
    bg: "#e6f4ff",
    border: "#91caff",
  },
  [ROLE_USER]: {
    label: "Aluno",
    color: "#09ac9c",
    bg: "#e6fffb",
    border: "#87e8de",
  },
  [ROLE_ADMIN_CUSTOMER]: {
    label: "Cliente",
    color: "#fa8c16",
    bg: "#fff7e6",
    border: "#ffd591",
  },
};

const getMethodInfo = (row) => {
  if (row.role !== ROLE_USER) {
    return null;
  }
  if (isEspotive()) {
    if (row.isBoot === true) {
      return { title: "Iniciação", color: "#d4b106", bg: "#fffbe6", border: "#ffe58f" };
    }
    return null;
  }
  if (row.isBoot === true) {
    return { title: "Atleta", color: "#09ac9c", bg: "#e6fffb", border: "#87e8de" };
  }
  if (row.isBoot === false) {
    return { title: "Kids", color: "#6c63ff", bg: "#f0f0ff", border: "#b3b0ff" };
  }
  if (row.isBoot === null) {
    return { title: "Adulto", color: "#595959", bg: "#fafafa", border: "#d9d9d9" };
  }
  return null;
};

export const getUserRowClassName = (record) => {
  const classes = [];
  const roleClass = {
    [ROLE_ADMIN]: "user-role-admin",
    [ROLE_USER_ADMIN]: "user-role-professor",
    [ROLE_USER]: "user-role-aluno",
    [ROLE_ADMIN_CUSTOMER]: "user-role-cliente",
  }[record.role];
  if (roleClass) {
    classes.push(roleClass);
  }
  const method = getMethodInfo(record);
  if (method) {
    const methodSlug = {
      Iniciação: "iniciacao",
      Atleta: "atleta",
      Kids: "kids",
      Adulto: "adulto",
    }[method.title];
    if (methodSlug) {
      classes.push(`user-method-${methodSlug}`);
    }
  }
  return classes.join(" ");
};

const ProfileTag = ({ role }) => {
  const style = ROLE_STYLES[role];
  if (!style) {
    return null;
  }
  return (
    <Tag
      style={{
        fontFamily: FONT,
        color: style.color,
        background: style.bg,
        borderColor: style.border,
        fontWeight: 600,
        margin: 0,
      }}
    >
      {style.label}
    </Tag>
  );
};

const MethodTag = ({ row }) => {
  const method = getMethodInfo(row);
  if (!method) {
    return null;
  }
  return (
    <Tag
      style={{
        fontFamily: FONT,
        color: method.color,
        background: method.bg,
        borderColor: method.border,
        fontWeight: 600,
        margin: 0,
      }}
    >
      {method.title}
    </Tag>
  );
};

export const ColumnsUser = () => {
  const table = [
    {
      title: "Foto",
      dataIndex: "image",
      width: "10%",
      render: (text, row) => {
        const style = ROLE_STYLES[row.role];
        return (
          <div className={`user-avatar user-avatar--${row.role?.toLowerCase()}`}>
            <Avatar
              src={row.img}
              icon={<UserRound size={50} />}
              size={50}
              style={
                style
                  ? { border: `2px solid ${style.color}`, background: style.bg }
                  : undefined
              }
              onError={(e) => console.log(e)}
            />
          </div>
        );
      },
    },
    {
      title: "Nome",
      dataIndex: "name",
      textWrap: "word-break",
      ellipsis: true,
      width: "10%",
      render: (text, row) => {
        const method = getMethodInfo(row);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span>{text}</span>
            {method && (
              <Tooltip style={{ fontFamily: FONT }} title={`Método ${method.title}`}>
                <Info
                  color={method.color}
                  size={16}
                  style={{ cursor: "pointer", flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const tableDefault = [
    {
      title: "E-mail",
      dataIndex: "email",
      textWrap: "word-break",
      width: "25%",
      ellipsis: true,
    },
    {
      title: "Data de Nascimento",
      dataIndex: "birthDate",
      textWrap: "word-break",
      width: "15%",
      ellipsis: true,
      render: (text, row) => {
        if (row.birthDate) {
          return dayjs(row.birthDate).format("DD/MM/YYYY");
        }
        return "";
      },
    },
    {
      title: "Perfil",
      dataIndex: "profile",
      textWrap: "word-break",
      width: "15%",
      ellipsis: true,
      render: (text, row) => <ProfileTag role={row.role} />,
    },
    {
      title: "Método",
      dataIndex: "method",
      width: "12%",
      ellipsis: true,
      render: (text, row) => <MethodTag row={row} />,
    },
  ];

  table.push(...tableDefault);

  table.push({
    title: "Ações",
    dataIndex: "action",
    width: "10%",

    render: (text, row) => {
      return (
        <Space size="middle">
          {row.edit && (
            <Tooltip style={{ fontFamily: FONT }} title="Editar Usuário">
              <UserRoundPen
                onClick={() => row.edit(row)}
                size={22}
                style={{ cursor: "pointer", color: "#09ac9c" }}
              />
            </Tooltip>
          )}
          {row.disable && (
            <Tooltip style={{ fontFamily: FONT }} title="Desativar Usuário">
              <UserRoundX
                onClick={() => row.disable(row)}
                size={22}
                style={{ cursor: "pointer", color: "#EC6D62" }}
              />
            </Tooltip>
          )}
          {row.reset && isAdmin() && (
            <Tooltip style={{ fontFamily: FONT }} title="Resetar Senha">
              <KeySquare
                onClick={() => row.reset(row)}
                size={22}
                style={{ cursor: "pointer", color: "#1677ff" }}
              />
            </Tooltip>
          )}
        </Space>
      );
    },
  });
  return table;
};
